import { IDevice } from '../interfaces/types.ts'

export type ActivityLevel = 'active' | 'degraded' | 'inactive' | 'unknown'

export type InactiveReason =
    | 'power_off'
    | 'sleeping'
    | 'charging'
    | 'power_saving'
    | 'overdue'

export type DegradedReason = 'invalid_gps'

export type ActivityReason = InactiveReason | DegradedReason | 'active' | 'unknown'

export interface ActivityStatus {
    level: ActivityLevel
    reason: ActivityReason
    lastContactTime: string | null
    lastValidLocationTime: string | null
    expectedInterval: number
    overdueBy: number | null
}

// Grace period: 50% of the expected interval, minimum 60 seconds
function getGracePeriod(intervalSeconds: number): number {
    return Math.max(intervalSeconds * 0.5, 60)
}

/**
 * Computes the activity status of a device based on its current state.
 * Priority order:
 * 1. power_off
 * 2. sleeping
 * 3. charging
 * 4. power_saving (beacon in range + power on)
 * 5. invalid_gps (messaging but no valid locations)
 * 6. overdue (hasn't messaged within expected interval + grace)
 * 7. active
 */
export function computeActivityStatus(device: IDevice): ActivityStatus {
    const status = device.status
    const interval = status.backgroundInterval || 900 // default 15 min
    const lastValidLocation = device.lastLocation?.properties?.receivedTime || null
    const lastReport = device.lastReport?.properties?.receivedTime || null

    // Use lastContact if available, otherwise fall back to the most recent report/location timestamp
    let lastContact = status.lastContact || null
    if (!lastContact) {
        if (lastReport && lastValidLocation) {
            lastContact = new Date(lastReport).getTime() > new Date(lastValidLocation).getTime()
                ? lastReport : lastValidLocation
        } else {
            lastContact = lastReport || lastValidLocation || null
        }
    }

    const base: ActivityStatus = {
        level: 'unknown',
        reason: 'unknown',
        lastContactTime: lastContact,
        lastValidLocationTime: lastValidLocation,
        expectedInterval: interval,
        overdueBy: null,
    }

    // No contact data at all
    if (!lastContact) {
        return base
    }

    // 1. Device powered off
    if (!status.powerOn) {
        return { ...base, level: 'inactive', reason: 'power_off' }
    }

    // 2. Device sleeping
    if (status.sleeping) {
        return { ...base, level: 'inactive', reason: 'sleeping' }
    }

    // 3. Device charging
    if (status.charging) {
        return { ...base, level: 'inactive', reason: 'charging' }
    }

    // 4. Power saving mode (beacon in range)
    if (status.beaconInRange && status.powerOn) {
        return { ...base, level: 'inactive', reason: 'power_saving' }
    }

    // Check if overdue
    const now = Date.now()
    const lastContactMs = new Date(lastContact).getTime()
    const grace = getGracePeriod(interval)
    const deadline = lastContactMs + (interval * 1000) + (grace * 1000)
    const isOverdue = now > deadline

    // 5. Device is messaging but GPS is invalid
    // Two ways to detect:
    //   a) lastReport is newer than lastLocation (fresh invalid report arrived)
    //   b) lastContact is significantly newer than lastLocation (device is checking in
    //      but its valid-location timestamp is stale — works on initial login)
    if (!isOverdue && lastValidLocation) {
        const validLocationMs = new Date(lastValidLocation).getTime()
        const reportMs = lastReport ? new Date(lastReport).getTime() : 0
        const gap = lastContactMs - validLocationMs
        // If either signal indicates invalid GPS, flag as degraded
        if (reportMs > validLocationMs || gap > (interval * 1000) + (grace * 1000)) {
            return { ...base, level: 'degraded', reason: 'invalid_gps' }
        }
    }

    // 6. Overdue
    if (isOverdue) {
        const overdueBy = Math.round((now - deadline) / 1000)
        return { ...base, level: 'inactive', reason: 'overdue', overdueBy }
    }

    // 7. Active
    return { ...base, level: 'active', reason: 'active' }
}
